import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import objectHash from "object-hash";
import { useEffect, useLayoutEffect, useMemo } from "react";
import { debounceTime, Subject } from "rxjs";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type {
  CancellationToken,
  ChartResponse,
  ConditionField,
  DimensionDescriptor,
  DimensionField,
  QueryLoggingItem,
  ReportField,
  RunChartReportRequest,
} from "../../../../../../../../api/types/svTypes.ts";
import { getAllFilters } from "../../../../utilities/getAllConditions.ts";
import { isPivotConfigurationValid } from "../../../../utilities/isConfigurationValid.ts";
import useDataLoadingViewer from "./useDataLoadingViewer.ts";
interface Props {
  dimensions: DimensionDescriptor[];
  conditions: ConditionField[];
  argumentFields: DimensionField[];
  values: ReportField[];
  refreshToken: string | undefined;
  requestConfig: RunChartReportRequest;
  runReport: (
    dataSetId: string,
    body: RunChartReportRequest,
    done: (data: ApiResponse<ChartResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  updateDevTools?: (loggingItems: QueryLoggingItem[] | undefined) => void;
  setEtag?: (etag: string | undefined, cacheValid: boolean | undefined) => void;
}
export default function useChartStateViewer({
  dimensions,
  conditions,
  argumentFields,
  values,
  refreshToken,
  requestConfig,
  runReport,
  updateDevTools,
  setEtag,
}: Props) {
  const { reportType } = useBiCommonDataContext();
  const subjectDelay = useMemo(() => new Subject<string>(), []);
  const { data, calculating, error, calculate, failedMeasures, initialized } = useDataLoadingViewer({
    requestConfig,
    runReport,
    updateDevTools,
    setEtag,
  });

  const configurationValidity = useMemo(() => {
    return isPivotConfigurationValid(conditions, argumentFields, values, dimensions, reportType);
  }, [argumentFields, conditions, dimensions, values, reportType]);

  const fieldsConfigured = useMemo(() => argumentFields.length > 0 && values.length > 0, [argumentFields, values]);

  const conditionsFiltersHash = useMemo(() => objectHash(getAllFilters(conditions, values)), [conditions, values]);

  useLayoutEffect(() => {
    const subscription = subjectDelay.pipe(debounceTime(100)).subscribe(() => calculate());
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (configurationValidity.valid && fieldsConfigured) {
      subjectDelay.next(Math.random().toString());
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [conditionsFiltersHash, refreshToken, fieldsConfigured, configurationValidity.valid]);

  return {
    data: data?.result,
    calculating,
    error,
    fieldsConfigured,
    configurationValidity,
    failedMeasures,
    initialized,
  };
}
